import { useState, useEffect, useRef } from 'react';
import { cn } from '@/shared/utils/cn';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { FloatingStoreVisitCtaIcon, FloatingStoreVisitCtaArrowIcon } from '@/shared/icons/icons';
import sparkleGif from '@/shared/assets/images/pos-cart/sparkle.gif';

export default function FloatingStoreVisitCta() {
  const [isExpanded, setIsExpanded] = useState(() => typeof window !== 'undefined' && window.scrollY === 0);
  const touchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { analytics, isInitialized } = useAnalytics();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExpanded(false);
    }, 5000);
    const handleScroll = () => {
    setIsExpanded(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });


    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToSection = () => {
    const section = document.getElementById('store-visit-cart-items-section');
    if (section) {
      try {
        const elementPosition = section.getBoundingClientRect().top;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const elementHeight = section.offsetHeight || 0;
        const viewportHeight = window.innerHeight || 0;
        const offsetPosition = elementPosition + scrollTop - (viewportHeight / 2) + (elementHeight / 2);

        const scrollOptions: ScrollToOptions = {
          top: offsetPosition,
          behavior: 'smooth',
        };

        window.scrollTo(scrollOptions);
        document.documentElement.scrollTo(scrollOptions);
        document.body.scrollTo(scrollOptions);
      } catch {
        section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    setIsExpanded(false);

    if (analytics && isInitialized) {
      analytics.trackEvent('Homepage Store-Visit Cart CTA Clicked', {
        'Page Name': 'Home',
      });
    }
  };

  const handleTouchStart = () => {
    touchTimerRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, 2000);
  };

  const handleTouchEnd = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  return (
    <div
      onClick={handleScrollToSection}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      className={cn(
        "fixed bottom-[33.33vh] right-0 z-50 flex items-center cursor-pointer transition-all duration-500 ease-in-out select-none",
        "h-14 text-white border-l border-y border-white/20 shadow-lg rounded-l-full overflow-hidden",
        "backdrop-blur-md",
        isExpanded ? "w-[319px] sm:w-[319px] md:w-[500px]" : "w-[60px]"
      )}
      style={{
        background: "rgba(98, 124, 165, 0.2)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0px 4px 5.6px 0px #00000040",
      }}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out",
            isExpanded ? "opacity-0 scale-75 pointer-events-none" : "opacity-100 scale-100"
          )}
        >
          <FloatingStoreVisitCtaIcon className="h-6 w-6 text-white" />
        </div>
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center gap-1.5 transition-all duration-500 ease-in-out whitespace-nowrap pl-4 pr-3.5",
            isExpanded ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4 pointer-events-none"
          )}
        >
          <img
            src={sparkleGif}
            alt="Sparkle"
            className="h-5 w-5 md:h-6 md:w-6 object-contain shrink-0"
          />
          <span className="text-[14px] sm:text-[14px] md:text-[24px] font-medium tracking-wide">
            View Designs You Selected In-Store
          </span>
          <FloatingStoreVisitCtaArrowIcon className="h-5 w-5 shrink-0" />
        </div>
      </div>
    </div>
  );
}
